import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import Detail from '@/creditflowresearch/ui/components/deals/DealListTable/components/DealBody/DealDetails/Detail';
import {
  getDealFilings,
  getDealPropertyGroups,
  mapTrancheComments,
  renderPropertyGroups,
} from './helpers';
import type IDeal from '@/creditflowresearch/domain/vo/IDeal';
import type { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import {
  IAbsActiveCalendarDealDetails,
} from '@/creditflowresearch/application/ActiveCalendar/Abs/vo/IAbsActiveCalendarViewItem';
import LoginOrSignUpPopover from '@/creditflowresearch/ui/common/components/LoginOrSignUpPopover';

import styles from './dealDetails.module.scss';

interface IDealDetailsProps {
  deal: IDeal | ICorporateDeal | IAbsActiveCalendarDealDetails,
  showDealDetailsByDefault?: boolean,
  expandDirection?: 'top' | 'bottom',
  // TODO: remove when the European market opening period ends
  isRestricted?: boolean,
}

const DealDetails = ({
  deal,
  showDealDetailsByDefault = false,
  expandDirection = 'bottom',
  // TODO: remove when the European market opening period ends
  isRestricted = false,
}: IDealDetailsProps) => {
  const [isDetailsBlockVisible, setIsDetailsBlockVisible] = useState(showDealDetailsByDefault);

  const filings = useMemo(
    () => getDealFilings(deal),
    [deal],
  );

  const trancheComments = useMemo(
    () => deal.tranches
          && deal.tranches.length
          && deal.tranches.map(mapTrancheComments)
            .filter((item) => !!item)
            .join('\n'),
    [deal.tranches],
  );

  const dealPropertyGroups = useMemo(
    () => getDealPropertyGroups(deal),
    [deal],
  );

  const showDetailsClickHandler = () => {
    setIsDetailsBlockVisible(!isDetailsBlockVisible);
  };

  const renderShowHideButton = () => (
    // TODO: remove the popover wrapper when the European market opening period ends
    <LoginOrSignUpPopover isRestricted={ isRestricted } placement="bottom">
      <div
        className={ cn(styles.showHideContainer, { [styles.isDisabled]: isRestricted }) }
        onClick={ isRestricted ? undefined : showDetailsClickHandler }
      >
        <a className={ styles.showHideBtn } data-test={ `${ isDetailsBlockVisible ? 'hide' : 'more' }Details` }>
          { isDetailsBlockVisible ? 'Hide' : 'More' } Details
        </a>
      </div>
    </LoginOrSignUpPopover>
  );

  const isDetailsVisible = [
    filings,
    trancheComments,
    deal.comments,
    deal.collateral,
    deal.guarantor,
    deal.originator,
    deal.riskRetention,
    deal.useOfProceeds,
    deal.volckerCompliance,
    dealPropertyGroups.length > 0,
  ].some((item) => !!item);

  const renderDetailsContent = () => (
    <div
      className={ styles.detailsHolder }
    >
      <Detail
        label="Filings"
        value={ filings }
      />
      <Detail
        label="Tranche Comments"
        value={ trancheComments }
      />
      <Detail
        label="Deal Comments"
        value={ deal.comments }
      />
      <Detail
        label="Collateral"
        value={ deal.collateral }
      />
      <Detail
        label="Guarantor(s)"
        value={ deal.guarantor }
      />
      <Detail
        label="Originator(s)"
        value={ deal.originator }
      />
      <Detail
        label="Risk Retention"
        value={ deal.riskRetention }
      />
      <Detail
        label="Use of Proceeds"
        value={ deal.useOfProceeds }
      />
      <Detail
        label="Volcker Compliance"
        value={ deal.volckerCompliance }
      />
      <Detail
        label="Relative Value"
        value={ deal.relativeValue }
        isHtml
      />

      { renderPropertyGroups(dealPropertyGroups) }
    </div>
  );

  const renderDetailsContainer = () => (
    <div
      className={ cn(
        styles.detailsContainer,
        {
          [styles.detailsContainerTop]: expandDirection === 'top',
          [styles.detailsContainerBottom]: expandDirection === 'bottom',
          [styles.isOpen]: isDetailsBlockVisible,
        },
      ) }
    >
      { renderDetailsContent() }
    </div>
  );

  return (
    <>
      { expandDirection === 'top' && renderDetailsContainer() }
      { isDetailsVisible && renderShowHideButton() }
      { expandDirection === 'bottom' && renderDetailsContainer() }
    </>
  );
};

export default React.memo(DealDetails);
